/**
 * MediaPanel React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// React
import React from 'react';

// Material-UI
import {withStyles} from '@material-ui/core/styles';
import PropTypes from "prop-types";

// application
import PdfPanel from "./PdfPanel";
import FallbackImage from "./FallbackImage";
import MediaDownloadButton from "./MediaDownloadButton";
import {getMediaType} from "../../utils";
import {patchServerUrl} from "../../config";


/**
 * MediaPanel visualizes a media item according to its type
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
export class MediaPanel extends React.Component {

    attachSource(url) {
        if (this.playbackElement) {
            this.playbackElement.setAttribute('src', url);
            // this.playbackElement.load();
        }
    }

    detachSource() {
        // some browsers will not clean up videos properly,
        // so as a workaround at least remove the sources
        // (which are children of the video element)
        if (this.playbackElement) {
            this.playbackElement.removeAttribute('src');
            // this.playbackElement.load();
        }
    }

    setPlaybackElement(element) {
        if (this.playbackElement !== element) {
            // this.detachSource();
            this.playbackElement = element;
            // this.attachSource(patchServerUrl(this.props.url));
        }
    }

    updateVolume() {
        if (this.playbackElement) {
            let volume = parseFloat(this.props.volume);
            if (!isNaN(volume)) {
                if (volume < 0.0) volume = 0.0;
                else if (volume > 100.0) volume = 100.0;
                this.playbackElement.volume = volume * 0.01;
            }
        }
    }

    updatePosition() {
        if (this.playbackElement) {
            let position = parseFloat(this.props.position);
            if (!isNaN(position)) {
                console.log("updating playback position", position);
                if (position < 0.0) position = 0.0;
                this.playbackElement.currentTime = position;
            }
        }
    }

    updatePausing() {
        if (this.playbackElement) {
            console.log("media panel pause", this.props.paused, Date.now());
            if (this.props.paused) this.playbackElement.pause();
            else this.playbackElement.play();
        }
    }

    restart() {
        if (this.playbackElement) {
            this.playbackElement.pause();
            this.playbackElement.currentTime = 0;
            this.playbackElement.load();
            this.updatePausing();
        }
    }

    componentDidMount() {
        // this.attachSource(this.props.url);
    }

    componentWillUnmount() {
        this.detachSource();
    }

    componentDidUpdate(prevProps) {

        if (this.props.volume !== prevProps.volume) {
            this.updateVolume();
        }

        if (this.props.position !== prevProps.position) {
            this.updatePosition();
        }

        if (this.props.restart !== prevProps.restart) {
            this.restart();
        }
        else if (this.props.paused !== prevProps.paused) {
            this.updatePausing();
        }
    }

    render() {

        const {classes, url} = this.props;

        if (!url) {
            return <div>NO URL</div>;
        }

        const mediaType = getMediaType(url);

        const conditionalAttributes = {}
        if (this.props.autoplay) {
            conditionalAttributes.autoPlay = !this.props.paused;
        }
        if (this.props.showControls) {
            conditionalAttributes.controls = true;
        }
        if (this.props.loop) {
            conditionalAttributes.loop = true;
        }
        if (this.props.volume != null && parseFloat(this.props.volume) === 0) {
            conditionalAttributes.muted = true;
        }

        switch(mediaType) {

            case "video":

                return <video
                    ref={element => {this.setPlaybackElement(element)}}
                    src={patchServerUrl(url)} // better than using "attachSource"?
                    className={this.props.full ? classes.fullsize : classes.other}
                    {...conditionalAttributes}
                    onLoadStart={() => {this.updateVolume(); this.updatePosition();}} // surprisingly volume cannot be set directly
                    onEnded={this.props.onPlaybackEnded}
                    onClick={(e) => {if(this.props.playOnClick === false) e.preventDefault();}} // preventDefault prevents video playback when clicking on video
                />;

            case "audio":

                return <audio
                    ref={element => {this.setPlaybackElement(element)}}
                    src={patchServerUrl(url)} // better than using "attachSource"?
                    className={classes.other}
                    {...conditionalAttributes}
                    onLoadStart={() => {this.updateVolume(); this.updatePosition();}} // surprisingly volume cannot be set directly
                    onEnded={this.props.onPlaybackEnded}
                />;

            case "image":

                this.setPlaybackElement(null);
                return <FallbackImage
                    className={this.props.full ? classes.fullimage : classes.image}
                    urls={[this.props.thumbnail, this.props.url]}
                    title={this.props.title}
                    onClick={this.props.onClick}
                />;

            case "pdf":

                this.setPlaybackElement(null);
                return <PdfPanel file={patchServerUrl(url)} />;

            default:

                if (this.props.otherFilesAsDownload) {
                    this.setPlaybackElement(null);
                    return this.props.otherFilesAsDownload && <MediaDownloadButton url={patchServerUrl(url)} />;
                }
                else { // quick hack: allow audio streaming from any URL
                    return <audio
                        ref={element => {this.setPlaybackElement(element)}}
                        src={patchServerUrl(url)} // better than using "attachSource"?
                        className={classes.other}
                        {...conditionalAttributes}
                        onLoadStart={() => {this.updateVolume(); this.updatePosition();}} // surprisingly volume cannot be set directly
                        onEnded={this.props.onPlaybackEnded}
                    />;
                }
        }
    }
}

MediaPanel.propTypes = {
    url: PropTypes.string.isRequired,
    title: PropTypes.string,
    showControls: PropTypes.bool,
    autoplay: PropTypes.bool,
    playOnClick: PropTypes.bool,
    paused: PropTypes.bool,
    volume: PropTypes.number,
    position: PropTypes.number,
    restart: PropTypes.any,
    loop: PropTypes.bool,
    otherFilesAsDownload: PropTypes.bool,
    onPlaybackEnded: PropTypes.func
};

MediaPanel.defaultProps = {
    autoplay: false,
    showControls: true
};

const styles = theme => ({
    image: {
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain"
    },
    fullimage: {
        display: "block",
        maxWidth: "100%",
        maxHeight: "100%",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)"
    },
    other: {
        maxWidth: "100%",
        maxHeight: "100%"
    },
    fullsize: {
        width: "100%",
        height: "100%"
    }
});

export default withStyles(styles)(MediaPanel);
